// src/helpers/user_data_cache_sync.js
//
// Single source of truth for WRITING the user_data caches. Every "we have new
// user_data, persist it" path funnels through enqueue_user_data_cache_sync so
// there is exactly one writer — no two code paths race the same localStorage
// slot or fire competing CORS postMessages.
//
// Stores written here:
//   - Parent-origin LS   (data-hub-user-data:{org} on e.g. client-organization.com)
//   - Action-origin LS   (same key on data-hub-action.com) via the hidden CORS iframe
//
// The action-origin write is a pure localStorage write inside the iframe — it does
// NOT hit the network unless a fresh jwt_cookie + event_uuid are supplied (submit
// path), in which case the iframe also POSTs /api/post/set_jwt_cookie. Init / URL-param
// / manual syncs pass user_data only, so they stay HTTP-free (important for ITP).
//
// Concurrency: a per-instance single-flight with merged-pending coalescing. If a
// sync is already running, incoming requests are queued and merged into one follow-up
// write when the current one finishes. That keeps a burst of `data-hub-new-user-data`
// events from racing localStorage while preserving fields carried only by a middle event.

import { is_plain_object, is_non_empty_object } from './index';
import { normalize_data_hub_privacy_policy_string } from './data_hub_privacy_policy';
import { white_list_outgoing_person_fields } from './static_vars';
import { merge_user_data_browser } from './user_data_smart_merge';
import {
	write_organization_jwt_cache_entry,
	write_organization_user_data_cache_entry,
} from './organization_jwt_local_storage_cache';
import { send_user_data_to_cors_iframe } from '../forms/cors_iframe';

// Per-instance single-flight state. WeakMap keeps it off the instance (no accidental
// serialization) and lets it be GC'd with the instance.
const cache_sync_state_by_instance = new WeakMap();

// `data-hub-new-user-data` events from these sources are NOT auto-persisted by the
// save listener — they manage their own cache writes/clears:
//   - init: load_user_data_from_jwt_url_params already persists the final init merge before
//     resolving user_data_loaded_promise; the event is for downstream UI/listeners only.
//   - forget_user: explicitly REMOVES the cache entry (privacy); auto re-persisting
//     the cleared/anon payload would fight it.
//   - form_submit: persists the post-merge superset explicitly (its event fires with
//     the raw server response BEFORE the wide merge, so the event detail is narrower
//     than what we actually want cached).
const CACHE_SYNC_SKIP_EVENT_SOURCES = new Set(['init', 'forget_user', 'form_submit']);

/**
 * Builds a deterministic signature of the outgoing-whitelist user_data (+ an optional
 * jwt marker) so we can skip re-sending an identical payload to the CORS iframe.
 * Iterating the fixed-order whitelist array (instead of Object.keys) keeps the key
 * order stable regardless of how the merged object was assembled, so a genuine change
 * always changes the signature (no false "unchanged"); at worst nested-object key
 * reordering causes a harmless extra send. Exported so emit-side dedup can reuse it.
 *
 * @param {Object|null|undefined} user_data
 * @param {string|null} [jwt_marker]
 * @returns {string|null}
 */
export function build_user_data_sync_signature(user_data, jwt_marker = null) {
	const ordered_fields = [];
	if (is_plain_object(user_data)) {
		for (const field_name of white_list_outgoing_person_fields) {
			if (Object.prototype.hasOwnProperty.call(user_data, field_name)) {
				ordered_fields.push([field_name, user_data[field_name]]);
			}
		}
	}
	try {
		return JSON.stringify({ u: ordered_fields, j: jwt_marker || null });
	} catch {
		return null;
	}
}

/**
 * Layers the two always-highest-priority explicit sources on top of an already-merged
 * base: URL params, then `data-hub-initial-user-data` (constructor config.user_data,
 * retained on the instance as `config_user_data`). Used by the async CORS-return path so
 * a late server/iframe payload can't clobber the publisher/link intent. Scalars from the
 * higher tier win; complexKeys union via merge_user_data_browser.
 *
 * `url_param_user_data` is passed in (recomputed by the caller from the already-cached
 * `data_hub_instance.url_params` via getURLUserData) rather than read off the instance —
 * keeps this module from importing get_process_user_data and avoids a new instance field.
 *
 * @param {Object} data_hub_instance
 * @param {Object} base_user_data
 * @param {Object|null} [url_param_user_data]
 * @returns {Promise<Object>}
 */
export async function apply_explicit_user_data_overrides(data_hub_instance = window?.data_hub, base_user_data = {}, url_param_user_data = null) {
	let result = is_plain_object(base_user_data) ? base_user_data : {};

	if (is_plain_object(url_param_user_data)) {
		result = await merge_user_data_browser(result, url_param_user_data);
	}
	if (is_plain_object(data_hub_instance?.config_user_data)) {
		result = await merge_user_data_browser(result, data_hub_instance.config_user_data);
	}

	return result;
}

/**
 * Merge queued cache-sync requests into one request that preserves every unique user_data
 * field while letting later scalar values win. Fresh JWT/cookie-set intent is sticky: if
 * any queued request includes jwt_cookie + event_uuid, the merged follow-up still sends the
 * cookie-set iframe payload after the currently running sync finishes.
 *
 * @param {Array<Object>} pending_requests
 * @returns {Promise<Object|null>}
 */
async function merge_pending_user_data_cache_sync_requests(pending_requests = []) {
	let merged_request = null;

	for (const pending_request of pending_requests) {
		if (!pending_request || typeof pending_request !== 'object') {
			continue;
		}

		if (!merged_request) {
			merged_request = { ...pending_request };
			if (is_plain_object(pending_request.user_data)) {
				merged_request.user_data = await merge_user_data_browser({}, pending_request.user_data);
			}
			continue;
		}

		if (is_plain_object(pending_request.user_data)) {
			const base_user_data = is_plain_object(merged_request.user_data)
				? merged_request.user_data
				: {};
			merged_request.user_data = await merge_user_data_browser(
				base_user_data,
				pending_request.user_data
			);
		}

		if (typeof pending_request.jwt_cookie === 'string' && pending_request.jwt_cookie.length > 0) {
			merged_request.jwt_cookie = pending_request.jwt_cookie;
		}
		if (pending_request.event_uuid) {
			merged_request.event_uuid = pending_request.event_uuid;
		}
		if (pending_request.source) {
			merged_request.source = pending_request.source;
		}
	}

	return merged_request;
}

/**
 * Performs one cache write. Not exported — callers use enqueue_user_data_cache_sync,
 * which serializes these so two never overlap.
 *
 * @param {Object} data_hub_instance
 * @param {{user_data?: Object, jwt_cookie?: string|null, event_uuid?: string|null, source?: string}} request
 * @returns {Promise<void>}
 */
async function run_user_data_cache_sync(data_hub_instance, request) {
	const error_function = data_hub_instance?.error_function || console.error;
	const { user_data, jwt_cookie, event_uuid } = request || {};

	try {
		// Parent LS — JWT (independent field; only when a fresh one is supplied).
		if (typeof jwt_cookie === 'string' && jwt_cookie.length > 0) {
			write_organization_jwt_cache_entry(data_hub_instance, jwt_cookie);
		}

		// Parent LS — wide user_data (smart-merges over the previous entry, so this
		// never narrows what's already cached). Returns the persisted (filtered+merged)
		// object, which is what we mirror to the action origin.
		let persisted_user_data = {};
		if (is_plain_object(user_data)) {
			persisted_user_data =
				(await write_organization_user_data_cache_entry(data_hub_instance, user_data)) || {};
		}

		// Action-origin LS — mirror via the CORS iframe (cross-origin only).
		const forms_domain = data_hub_instance?.forms_domain || null;
		const is_cross_origin =
			typeof window !== 'undefined' && forms_domain && forms_domain !== window.location.origin;
		if (!is_cross_origin) {
			return;
		}

		const user_data_to_mirror = is_non_empty_object(persisted_user_data)
			? persisted_user_data
			: (is_plain_object(user_data) ? user_data : {});

		const has_cookie_set_intent =
			typeof jwt_cookie === 'string' && jwt_cookie.length > 0 && Boolean(event_uuid);

		if (has_cookie_set_intent) {
			// Submit path: the iframe must POST set_jwt_cookie, which we can't diff
			// locally, so always send. Refresh the signature so the next user_data-only
			// sync doesn't immediately re-send the same payload.
			data_hub_instance._last_cors_user_data_signature =
				build_user_data_sync_signature(user_data_to_mirror, jwt_cookie);

			send_user_data_to_cors_iframe(
				forms_domain,
				user_data_to_mirror,
				jwt_cookie,
				event_uuid,
				data_hub_instance
			);

			return;
		}

		// User_data-only mirror (init / URL params / manual): LS write inside the iframe,
		// no HTTP. Skip when nothing changed since the last mirror.
		if (!is_non_empty_object(user_data_to_mirror)) {
			return;
		}
		const signature = build_user_data_sync_signature(user_data_to_mirror, null);
		if (signature && signature === data_hub_instance._last_cors_user_data_signature) {
			return;
		}
		data_hub_instance._last_cors_user_data_signature = signature;
		send_user_data_to_cors_iframe(forms_domain, user_data_to_mirror, null, null, data_hub_instance);
	} catch (error) {
		error_function?.('Error syncing user_data caches: ' + error, null);
	}
}

/**
 * Canonical cache writer. Persists user_data (and an optional fresh JWT) to the parent-origin
 * LS and mirrors it to the action-origin LS via the CORS iframe. Serialized per instance
 * with merged-pending coalescing so concurrent callers can't race or drop middle-event fields.
 *
 * @param {Object} data_hub_instance
 * @param {{user_data?: Object, jwt_cookie?: string|null, event_uuid?: string|null, source?: string}} request
 * @returns {Promise<void>}
 */
export async function enqueue_user_data_cache_sync(data_hub_instance = window?.data_hub, request = {}) {
	if (!data_hub_instance) {
		return;
	}
	if (normalize_data_hub_privacy_policy_string(data_hub_instance?.privacy_policy) === 'none') {
		return;
	}

	let state = cache_sync_state_by_instance.get(data_hub_instance);
	if (!state) {
		// Scope: this single-flight only guards the localStorage cache sync: parent LS write
		// and action-origin LS mirror. It does not gate POST /api/post/user_data (form submit)
		// or POST /api/post/web (analytics), which have independent network handling.
		state = { is_running: false, pending_requests: [] };
		cache_sync_state_by_instance.set(data_hub_instance, state);
	}

	// A run is in flight. Queue synchronously so a request cannot be lost if the running
	// sync finishes before an async merge completes; the running loop drains and merges the
	// queue after its current write settles.
	if (state.is_running) {
		state.pending_requests.push(request);
		return;
	}

	state.is_running = true;
	let current_request = request;
	try {
		while (current_request) {
			await run_user_data_cache_sync(data_hub_instance, current_request);
			if (state.pending_requests.length === 0) {
				current_request = null;
				continue;
			}
			const pending_requests_to_merge = state.pending_requests;
			state.pending_requests = [];
			current_request = await merge_pending_user_data_cache_sync_requests(pending_requests_to_merge);
		}
	} finally {
		state.is_running = false;
	}
}

// Idempotent: module-level flag prevents adding the listener more than once even though
// register_user_data_cache_sync_listener() runs from every DataHub constructor. Same
// pattern as tel/telephone_sms_inputs.js `set_listener_for_new_user_data`.
let _cache_sync_listener_bound = false;

	/**
	 * Registers the SAVE-direction listener (once globally): any `data-hub-new-user-data`
	 * event except self-managed init / forget_user / form_submit sources is mirrored into the
	 * parent LS + action-origin LS via enqueue_user_data_cache_sync. This is the hook that
	 * persists URL-param updates, CORS-return updates, and host-app `data_hub.user_data` changes.
 *
 * Strictly write-only — never re-merges user_data, re-prefills forms, or re-dispatches the
 * event, so it can't cause flicker or loops. The target instance is read from the event
 * detail (falling back to window.data_hub), so a single global listener correctly serves
 * whichever instance dispatched — including, in theory, separate per-org instances, since
 * enqueue's single-flight is keyed per instance and the LS key is per organization_id.
 *
 * @returns {void}
 */
export function register_user_data_cache_sync_listener() {
	if (_cache_sync_listener_bound || typeof document === 'undefined') {
		return;
	}
	_cache_sync_listener_bound = true;

	document.addEventListener('data-hub-new-user-data', (event) => {
		const detail = event?.detail;
		if (!is_plain_object(detail)) {
			return;
		}
		if (CACHE_SYNC_SKIP_EVENT_SOURCES.has(detail.source)) {
			return;
		}
		const data_hub_instance = detail.data_hub_instance || window?.data_hub;
		if (!data_hub_instance) {
			return;
		}
		enqueue_user_data_cache_sync(data_hub_instance, {
			user_data: detail.user_data,
			jwt_cookie: detail.jwt_cookie || null,
			event_uuid: detail.event_uuid || null,
			source: detail.source || 'data-hub-new-user-data',
		});
	});
}
